import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Checkbox, cn, Progress, Tooltip } from '@sib/ui';
import {
  DoneIcon,
  DownloadIcon,
  ErrorIcon,
  TrashIcon,
} from '@spendbrain/icons';
import { FileItem } from '@spendbrain/types/files';
import { useFileUploadItem } from './FileUploadItem.utils';

interface FileUploadItemProps {
  fileItem: FileItem;
  onUpdate: (updatedFileItem: FileItem) => void;
  onRemove: (fileId: string) => void;
  loading?: boolean;
}

export const FileUploadItem = ({
  fileItem,
  onUpdate,
  onRemove,
  loading = false,
}: FileUploadItemProps) => {
  const { t } = useTranslation();

  const {
    isUploaded,
    isError,
    isFileUploading,
    isFileDownloadable,
    getUploadPhase,
  } = useFileUploadItem(fileItem);
  const fileNameRef = useRef<HTMLParagraphElement>(null);

  const isFileNameOverflowing = fileNameRef.current
    ? fileNameRef.current.offsetHeight < fileNameRef.current.scrollHeight ||
      fileNameRef.current.offsetWidth < fileNameRef.current.scrollWidth
    : false;

  return (
    <Card className="grid w-full gap-2 p-[14px]">
      <div className="flex w-full min-w-0 items-center justify-between gap-2 text-sm">
        <div className="flex min-w-0 flex-[1_1] items-center gap-2">
          <Checkbox
            checked={fileItem.selected}
            onCheckedChange={() =>
              onUpdate({ ...fileItem, selected: !fileItem.selected })
            }
            disabled={loading}
          />
          <div className="flex min-w-0 flex-[1_1] flex-col">
            <div className="flex min-w-0 items-center gap-2">
              {isUploaded ? (
                <DoneIcon className="text-secondary size-5 shrink-0" />
              ) : isError ? (
                <ErrorIcon className="size-5 shrink-0 text-[#F3486D]" />
              ) : null}
              <Tooltip
                TooltipTriggerProps={{ asChild: true }}
                TooltipContentProps={{
                  className: 'max-w-[420px] wrap-break-word',
                }}
                content={isFileNameOverflowing && fileItem.file.name}
              >
                <p
                  ref={fileNameRef}
                  className="min-w-0 flex-[1_1] truncate text-[#667080]"
                >
                  {fileItem.file.name}
                </p>
              </Tooltip>
              {fileItem.errorMessage && (
                <p className="ml-auto text-xs break-all text-[#F3486D] sm:text-base">
                  {fileItem.errorMessage}
                </p>
              )}
            </div>
            {isFileUploading && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Progress value={fileItem.progress} className="h-2 flex-1" />
                  <span className="text-muted-foreground text-xs">
                    {`${fileItem.progress}%`}
                  </span>
                </div>
                <span className="text-muted-foreground text-xs">
                  {getUploadPhase()}
                </span>
              </div>
            )}
          </div>
        </div>

        {!isUploaded && !isFileDownloadable && (
          <Button
            variant="ghost"
            onClick={() => onRemove(fileItem.id)}
            loading={isFileUploading}
            disabled={loading}
            type="button"
          >
            <TrashIcon className="text-primary" />
          </Button>
        )}
        {isFileDownloadable && (
          <Button variant="ghost" type="button" onClick={() => {}}>
            {/* TODO: add download logic */}
            <DownloadIcon className="text-primary size-5" />
          </Button>
        )}
      </div>
    </Card>
  );
};
